import getTimeZoneMessageValue from 'src/internal/display-name-formatter/getTimeZoneMessageValue';
import getLocaleData from 'src/internal/getLocaleData';
import matchesTimezoneFilters from 'src/public/timezoneFilters/timezoneFilters';
import {timezoneMessages} from 'src/public/timezoneMessages/timezoneMessages';

import type {IReports} from '@sail/observability';
import type {TimezoneFilters} from 'src/public/timezoneFilters/timezoneFilters';
import type {IntlConfig} from 'src/types';

/**
 * Returns a map of IANA timezone IDs to their localized display names.
 *
 * The keys are what `timezoneMessages` carries, which is not the same as what the tz database
 * carries: `Europe/Kyiv` and `America/Nuuk` have no label yet, while `Factory` and `US/Pacific-New`
 * have one without being current zones. Do not use this as a validation allowlist — a valid id can
 * be missing from it.
 *
 * Unfiltered it is still more than a picker should offer: `EST`, which a Java service rejects on
 * save, `Factory`, which is not a place, the `Etc/GMT±N` ids whose sign reads backwards, nine
 * spellings of UTC, and "Africa - Asmera" directly under "Africa - Asmara". Pass `filters` to
 * narrow it — a picker usually wants all three.
 *
 * @see https://trailhead.corp.stripe.com/docs/sail-intl/api/useIntl#get-timezone-labels
 */
export default function getTimezoneLabels(
  /** The intl config object containing the locale data. When using this API through the `useIntl` hook, this config object is automatically injected. */
  config: IntlConfig,
  reports: IReports,
  /** Which ids to include. Nothing set means every id the tz database carries. */
  filters: TimezoneFilters = {},
): Map<string, string> {
  const localeData = getLocaleData(config.locale, config, reports);

  return new Map(
    Object.entries(timezoneMessages)
      .filter(([tzId]) => matchesTimezoneFilters(tzId, filters))
      .map(([tzId, descriptor]) => [
        tzId,
        getTimeZoneMessageValue(localeData, descriptor),
      ]),
  );
}

export type GetTimezoneLabels = typeof getTimezoneLabels;
export type GetTimezoneLabelsDeclarative = (
  filters?: TimezoneFilters,
) => Map<string, string>;
